/**
 * budgetNow.ts — what she has RIGHT NOW, and what she can plan on.
 *
 * In `_shared/` because the My Budget screen shows this as its headline and
 * `kin-chat` answers "what have I got right now" with it, and a second copy of
 * the arithmetic is how those two start disagreeing about the same Tuesday. The
 * badge drifting from the sheet in 6fd0c241 is what that looks like in practice.
 *
 * ⚠️ Relative imports with `.ts` extensions, no path aliases, no React. Deno
 * resolves this file directly and cannot reach into `src/`.
 *
 * ── WHY THIS EXISTS ────────────────────────────────────────────────────────
 *
 * "Left over" is a whole-MONTH projection: on the 2nd it already counts a
 * paycheck that has not landed and bills that have not come out. It answers
 * "how will this month end", which is a real question and not the one she asks
 * when she is standing in a shop.
 *
 * The thing that USED to occupy this slot was a per-paycheck leftover
 * (`amount - its matched bills`) on each paycheck card. That number is wrong in
 * the expensive direction: it ignores spending transactions and bills with no
 * due day, so the per-paycheck figures sum HIGHER than the month's real
 * leftover. It was removed from kin-chat in #1928 and this module is what
 * replaces it on screen.
 *
 * ── THE ASSUMPTIONS, which are assumptions and not facts ───────────────────
 *
 *   1. Paychecks land ON their pay day.
 *   2. Bills come out ON their due day.
 *   3. A bill with NO due day counts from the 1st. Deliberately the pessimistic
 *      reading: it treats the money as already gone, so the figure errs LOW.
 *   4. Each month starts at $0. NO ROLLOVER. Last month's leftover does not
 *      carry in.
 *
 * ⚠️ (4) is the one most likely to be misread by whoever renders this. It is
 * "what has moved through this month's budget so far", NOT a bank balance. Copy
 * must never imply it is one.
 *
 * ── THE DIRECTION OF EVERY DOUBT ───────────────────────────────────────────
 *
 * Where a value is unreadable or a timing is unknowable, this module errs LOW,
 * because a figure that is too high is the one that makes her spend money she
 * does not have. Concretely: a paycheck with no pay day is NOT counted as
 * arrived, and a transaction with an unreadable date counts if it is spending
 * and does not if it is income.
 */

import { billsCountingInMonth } from "./billExpiry.ts";
import {
  resolveDueDay,
  resolvePayDay,
  type MatchableBill,
  type MatchablePaycheck,
} from "./billPaycheckMatch.ts";
import { splitISODate } from "./payDay.ts";

/** A transaction as both surfaces read it. `transaction_date` is a `date`. */
export interface NowTransaction {
  amount: number | string;
  /** "income" or "expense", as `personal_budget_transactions.type` stores it. */
  type: string;
  transaction_date: string | null;
}

/** A bill that also carries its end date, so the expiry rule can be applied. */
export type NowBill = MatchableBill & { expires_at?: string | null };

export interface BudgetNow {
  /**
   * Money in so far minus money out so far, under the assumptions above.
   *
   * ⚠️ `null` means NOT COMPUTABLE, and a caller must then show no number at
   * all rather than a zero. See `unavailable`.
   */
  rightNow: number | null;
  /**
   * `rightNow` minus the bills still to come before the next paycheck.
   *
   * The spendable figure, and the one worth showing next to the other: "right
   * now" alone invites her to spend money that rent needs.
   */
  planOn: number | null;
  /**
   * Why the figures are null, or null when they are fine.
   *
   * "no-pay-days": not one paycheck has a `pay_date`, so nothing can be placed
   * in time. Bills would still subtract while no paycheck counted as arrived,
   * which would render a confident and badly negative number.
   */
  unavailable: null | "no-pay-days";

  // ── Components, so a caller can explain the figure instead of asserting it ──
  /** Paychecks whose pay day has passed. */
  paychecksIn: number;
  /** Income transactions dated today or earlier. */
  incomeIn: number;
  /** Bills whose due day has passed, expired ones excluded. */
  billsOut: number;
  /** Spending transactions dated today or earlier. */
  spendingOut: number;
  /** Bills still to come before the next paycheck. The `planOn` deduction. */
  stillToComeOut: number;
  /**
   * The part of `stillToComeOut` owed by bills with NO due day.
   *
   * ⚠️ Exposed so a caller can reconcile two numbers that otherwise look
   * contradictory. `billsDueBeforeNextPayday` builds a LIST and can only list
   * bills it can place in time, so it omits undated ones; this module counts
   * them from the 1st (assumption 3). Without this field a caller renders
   * "$250 still due" above a list of bills totalling $210 and looks broken.
   */
  stillToComeUndated: number;
  /** The day of the month the next paycheck lands, or null. */
  nextPayDay: number | null;
  /** That pay day falls in the following month. */
  nextPayDayWraps: boolean;
  /** Today, as passed in. Echoed so a caller can say what "now" meant. */
  todayDay: number;
}

const num = (v: unknown): number => {
  const n = typeof v === "number" ? v : Number.parseFloat(String(v));
  return Number.isFinite(n) ? n : 0;
};

/** Year and month as one comparable integer. Same shape as billExpiry's. */
const monthOrdinal = (p: { y: number; m: number }): number => p.y * 12 + (p.m - 1);

/**
 * Has this transaction already happened, as of `todayDay` in `monthISO`?
 *
 * ⚠️ Compares the MONTH as well as the day. Rows are month-scoped by
 * `budget_id`, so in practice the months match, but comparing days alone would
 * silently misread any row that is not where it is expected: a transaction
 * dated the 28th of LAST month would look like it had not happened yet.
 *
 * A row dated before this month HAS happened, so it counts.
 */
function hasHappened(
  transactionDate: string | null | undefined,
  monthISO: string,
  todayDay: number,
): boolean | null {
  if (!transactionDate) return null;
  const t = splitISODate(transactionDate);
  const m = splitISODate(monthISO);
  if (!t || !m) return null;
  const to = monthOrdinal(t);
  const mo = monthOrdinal(m);
  if (to < mo) return true; // an earlier month: already gone
  if (to > mo) return false; // a later month: not yet
  return t.d <= todayDay;
}

/**
 * The day a bill comes out, for the purposes of "has it happened".
 *
 * ⚠️ A bill with NO due day counts from the 1st, which means "already out".
 * That is assumption 3 and it is pessimistic on purpose: it lowers the figure,
 * and a figure that is too low costs her nothing while one that is too high
 * costs her money. It is also the opposite of the defect this module replaces,
 * where undated bills were omitted from a per-paycheck leftover entirely and
 * pushed it higher.
 */
function billOutDay(bill: NowBill, monthISO: string): number {
  const due = resolveDueDay(bill.due_day, monthISO);
  return due === null ? 1 : due;
}

/**
 * What she has right now, and what she can plan on.
 *
 * `todayDay` is the day of the month in HER timezone. It is passed in rather
 * than read from a clock so this module takes no clock at all: the same inputs
 * always give the same answer, which is what makes it testable and what stops
 * the two surfaces disagreeing because one of them asked a moment later.
 */
export function budgetNow<P extends MatchablePaycheck, B extends NowBill, T extends NowTransaction>(
  paychecks: P[],
  bills: B[],
  transactions: T[],
  monthISO: string,
  todayDay: number,
): BudgetNow {
  // The expiry rule FIRST, so a bill that finished in an earlier month is not
  // money going out of this one. Same helper the sheet and kin-chat draw with.
  const live = billsCountingInMonth(bills, monthISO);

  const anchors = paychecks
    .map((p) => resolvePayDay(p, monthISO))
    .filter((d): d is number => d !== null)
    .sort((a, b) => a - b);

  const ahead = anchors.filter((d) => d > todayDay);
  const nextPayDayWraps = anchors.length > 0 && ahead.length === 0;
  const nextPayDay = anchors.length === 0 ? null : nextPayDayWraps ? anchors[0] : ahead[0];

  // ⚠️ A paycheck with NO pay day is never counted as arrived. Counting one
  // would inflate the figure on the strength of a timing nobody recorded.
  const paychecksIn = paychecks.reduce((t, p) => {
    const day = resolvePayDay(p, monthISO);
    return day !== null && day <= todayDay ? t + num(p.amount) : t;
  }, 0);

  const billsOut = live.reduce(
    (t, b) => (billOutDay(b, monthISO) <= todayDay ? t + num(b.amount) : t),
    0,
  );

  // ⚠️ THE planOn WINDOW IS STRICTLY AFTER TODAY, and that is NOT the same
  // window as `billsDueBeforeNextPayday`'s.
  //
  // That function includes bills due TODAY on purpose: it builds a LIST, and an
  // unpaid bill left off a list is the costly error. Here the arithmetic has
  // already removed today's bills via `billsOut` (assumption 2, due day <=
  // today), so counting them again would subtract them twice and understate
  // `planOn` by a whole bill. Two questions, two windows, deliberately.
  const inStillToCome = (b: NowBill): boolean => {
    if (nextPayDay === null) return false;
    const day = billOutDay(b, monthISO);
    return nextPayDayWraps
      ? day > todayDay || day < nextPayDay
      : day > todayDay && day < nextPayDay;
  };
  const stillToComeOut = live.reduce((t, b) => (inStillToCome(b) ? t + num(b.amount) : t), 0);
  const stillToComeUndated = live.reduce(
    (t, b) =>
      resolveDueDay(b.due_day, monthISO) === null && inStillToCome(b) ? t + num(b.amount) : t,
    0,
  );

  let incomeIn = 0;
  let spendingOut = 0;
  for (const tx of transactions) {
    const happened = hasHappened(tx.transaction_date, monthISO, todayDay);
    const isIncome = tx.type === "income";
    if (happened === null) {
      // ⚠️ An unreadable date resolves in the direction that cannot cost her:
      // spending counts, income does not. Asymmetric on purpose, and the only
      // asymmetry in this module.
      if (!isIncome) spendingOut += num(tx.amount);
      continue;
    }
    if (!happened) continue;
    if (isIncome) incomeIn += num(tx.amount);
    else spendingOut += num(tx.amount);
  }

  // ⚠️ With no pay day anywhere, neither figure is computable. Bills would
  // subtract while no paycheck counted as arrived, rendering a confident and
  // badly negative number out of missing data rather than out of her finances.
  // The components are still returned, so a caller can show the nudge and
  // still say something true.
  if (anchors.length === 0) {
    return {
      rightNow: null,
      planOn: null,
      unavailable: "no-pay-days",
      paychecksIn,
      incomeIn,
      billsOut,
      spendingOut,
      stillToComeOut,
      stillToComeUndated,
      nextPayDay: null,
      nextPayDayWraps: false,
      todayDay,
    };
  }

  const rightNow = paychecksIn + incomeIn - billsOut - spendingOut;
  return {
    rightNow,
    planOn: rightNow - stillToComeOut,
    unavailable: null,
    paychecksIn,
    incomeIn,
    billsOut,
    spendingOut,
    stillToComeOut,
    stillToComeUndated,
    nextPayDay,
    nextPayDayWraps,
    todayDay,
  };
}
